<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title>
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          {{ $t('golf_courts') }}
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialog = true">
            + {{ $t('add_golf_court') }}
          </v-btn>
        </v-card-title>

        <v-data-table :headers="headers" :items="courts" :loading="loading" class="elevation-1"
          :no-data-text="$t('no_records_yet')">
          <template #item.status="{ item }">
            <v-chip :color="getStatusColor(item.status)" dark small>
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon small class="mr-2" v-bind="attrs" v-on="on" @click="viewDetails(item)">
                  mdi-eye
                </v-icon>
              </template>
              <span>{{ $t('view_details') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon small class="mr-2" v-bind="attrs" v-on="on" @click="editItem(item)">
                  mdi-pencil
                </v-icon>
              </template>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" @click="confirmDelete(item)">
                  mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('delete_action') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!-- Dialog para crear o editar un campo -->
    <v-dialog v-model="dialog" max-width="700px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedItem.name" :label="$t('golf_court_name')"
                    :rules="[rules.required]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editedItem.course_type" :items="courseTypes" item-text="text" item-value="value"
                    :label="$t('golf_court_type')" :rules="[rules.required]"></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="editedItem.total_holes" type="number" :label="$t('total_holes')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="editedItem.hole_range_start" type="number" :label="$t('hole_range_start')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="editedItem.hole_range_end" type="number" :label="$t('hole_range_end')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedItem.minutes_per_hole" type="number" :label="$t('minutes_per_hole')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedItem.time_interval" type="number" :label="$t('time_interval')"
                    :rules="[rules.required, rules.positiveNumber]"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu ref="menuStart" v-model="menuStart" :close-on-content-click="false" :nudge-right="40"
                    :return-value.sync="editedItem.opening_time" transition="scale-transition" offset-y
                    max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="editedItem.opening_time" :label="$t('opening_time')"
                        prepend-icon="mdi-clock-time-four-outline" readonly v-bind="attrs" v-on="on"
                        :rules="[rules.required]"></v-text-field>
                    </template>
                    <v-time-picker v-if="menuStart" v-model="editedItem.opening_time" full-width
                      @click:minute="$refs.menuStart.save(editedItem.opening_time)"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu ref="menuEnd" v-model="menuEnd" :close-on-content-click="false" :nudge-right="40"
                    :return-value.sync="editedItem.closing_time" transition="scale-transition" offset-y
                    max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="editedItem.closing_time" :label="$t('closing_time')"
                        prepend-icon="mdi-clock-time-four-outline" readonly v-bind="attrs" v-on="on"
                        :rules="[rules.required]"></v-text-field>
                    </template>
                    <v-time-picker v-if="menuEnd" v-model="editedItem.closing_time" full-width
                      @click:minute="$refs.menuEnd.save(editedItem.closing_time)"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <p class="caption grey--text mb-2">{{ $t('opening_days') }}</p>
                  <v-btn-toggle v-model="selectedDays" multiple mandatory>
                    <v-btn v-for="(day, index) in daysOfWeek" :key="index" :value="index.toString()" text>
                      {{ $t(day) }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
                <v-col cols="12">
                  <v-select v-model="editedItem.status" :items="courtStatuses" item-text="text" item-value="value"
                    :label="$t('estado')" :rules="[rules.required]" outlined dense></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="close">{{ $t('cancelar') }}</v-btn>
          <v-btn color="blue darken-1" text @click="save" :disabled="!valid">{{ $t('guardar') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for Details -->
    <v-dialog v-model="dialogDetails" max-width="600px">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ $t('golf_court_details') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogDetails = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-list dense>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('nombre') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.name }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('golf_court_type') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ getCourseTypeName(viewItem.course_type) }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('total_holes') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.total_holes }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('hole_range') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.hole_range_start }} - {{ viewItem.hole_range_end
                }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('schedule') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.opening_time }} - {{ viewItem.closing_time
                }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('minutes_per_hole') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.minutes_per_hole }} min</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('time_interval') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ viewItem.time_interval }} min</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('days_open') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">{{ formatDays(viewItem.opening_days) }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content><strong>{{ $t('estado') }}:</strong></v-list-item-content>
              <v-list-item-content class="align-end">
                <v-chip :color="getStatusColor(viewItem.status)" dark small>
                  {{ getStatusText(viewItem.status) }}
                </v-chip>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="dialogDelete" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('delete_golf_court_title') }}</v-card-title>
        <v-card-text>{{ $t('delete_golf_court_confirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDelete">{{ $t('cancelar') }}</v-btn>
          <v-btn color="red darken-1" text @click="deleteItemConfirm">{{ $t('delete_action') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://clubtest.intrare.app'
const IdSport = '91b08c78-baf5-49ab-b488-36af5867fb3b'
const IdHousingUnit = 'a58d8679-e2ae-4055-a243-c63d438f19eb'

export default {
  name: 'GolfCourts',
  data: () => ({
    dialog: false,
    dialogDetails: false,
    dialogDelete: false,
    loading: false,
    valid: true,
    menuStart: false,
    menuEnd: false,
    itemToDelete: null,
    courts: [],
    editedIndex: -1,
    editedItem: {
      idSport: IdSport,
      IdHousingUnit: IdHousingUnit,
      name: '',
      course_type: 2,
      total_holes: 18,
      hole_range_start: 1,
      hole_range_end: 18,
      minutes_per_hole: 10,
      time_interval: 10,
      opening_days: '0,1,2,3,4,5,6',
      opening_time: '06:00:00',
      closing_time: '22:00:00',
      status: 1
    },
    defaultItem: {
      idSport: IdSport,
      IdHousingUnit: IdHousingUnit,
      name: '',
      course_type: 2,
      total_holes: 18,
      hole_range_start: 1,
      hole_range_end: 18,
      minutes_per_hole: 10,
      time_interval: 10,
      opening_days: '0,1,2,3,4,5,6',
      opening_time: '06:00:00',
      closing_time: '22:00:00',
      status: 1
    },
    viewItem: {},
    daysOfWeek: ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'],
    selectedDays: []
  }),

  computed: {
    formTitle () {
      return this.editedIndex === -1 ? this.$t('new_golf_court') : this.$t('edit_golf_court')
    },
    headers () {
      return [
        { text: this.$t('nombre'), value: 'name' },
        { text: this.$t('total_holes'), value: 'total_holes' },
        { text: this.$t('opening_time'), value: 'opening_time' },
        { text: this.$t('closing_time'), value: 'closing_time' },
        { text: this.$t('estado'), value: 'status' },
        { text: this.$t('acciones'), value: 'actions', sortable: false }
      ]
    },
    courseTypes () {
      return [
        { text: this.$t('standard'), value: 1 },
        { text: this.$t('professional'), value: 2 },
        { text: this.$t('practice'), value: 3 }
      ]
    },
    rules () {
      return {
        required: value => !!value || this.$t('required_field'),
        positiveNumber: value => value > 0 || this.$t('positive_number_error')
      }
    },
    courtStatuses () {
      return [
        { text: this.$t('court_status_open'), value: 1 },
        { text: this.$t('court_status_rain'), value: 2 },
        { text: this.$t('court_status_storm'), value: 3 },
        { text: this.$t('court_status_minor_damage'), value: 4 },
        { text: this.$t('court_status_damaged_greens'), value: 5 },
        { text: this.$t('court_status_maintenance'), value: 6 }
      ]
    }
  },

  created () {
    this.initializeComponent()
  },

  watch: {
    dialog (val) {
      val || this.close()
    },
    dialogDelete (val) {
      val || this.closeDelete()
    },
    selectedDays (val) {
      if (val) {
        this.editedItem.opening_days = val.join(',')
      }
    }
  },

  methods: {

    async initializeComponent () {
      await this.getCourts()
    },

    async getCourts () {
      this.loading = true
      try {
        const response = await this.apiCall('get', '/golf-courses/')
        console.log(response.data)
        this.courts = response.data
      } catch (error) {
        console.error('Error fetching courts:', error)
      } finally {
        this.loading = false
      }
    },

    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }

      if (data) {
        config.data = data
      }

      return axios(config)
    },

    editItem (item) {
      this.editedIndex = this.courts.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.selectedDays = item.opening_days ? item.opening_days.split(',') : []
      this.dialog = true
    },

    viewDetails (item) {
      this.viewItem = Object.assign({}, item)
      this.dialogDetails = true
    },

    confirmDelete (item) {
      this.itemToDelete = item
      this.dialogDelete = true
    },

    async deleteItemConfirm () {
      if (this.itemToDelete) {
        this.loading = true
        try {
          await this.apiCall('delete', `/golf-courses/${this.itemToDelete.id}/`)
          const index = this.courts.indexOf(this.itemToDelete)
          this.courts.splice(index, 1)
        } catch (error) {
          console.error('Error deleting court:', error)
        } finally {
          this.loading = false
          this.closeDelete()
        }
      }
    },

    closeDelete () {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.itemToDelete = null
      })
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
        this.selectedDays = this.defaultItem.opening_days.split(',')
        this.$refs.form.resetValidation()
      })
    },

    async save () {
      if (!this.$refs.form.validate()) return

      this.loading = true
      try {
        if (this.editedIndex > -1) {
          // Editar
          const response = await this.apiCall('put', `/golf-courses/${this.editedItem.id}/`, this.editedItem)
          Object.assign(this.courts[this.editedIndex], response.data)
        } else {
          // Crear
          const response = await this.apiCall('post', '/golf-courses/', this.editedItem)
          this.courts.push(response.data)
        }
        this.close()
      } catch (error) {
        console.error('Error saving court:', error)
      } finally {
        this.loading = false
      }
    },

    getCourseTypeName (value) {
      const type = this.courseTypes.find(t => t.value === value)
      return type ? type.text : this.$t('unknown')
    },

    formatDays (daysString) {
      if (!daysString) return ''
      const days = daysString.split(',')
      return days.map(d => this.$t(this.daysOfWeek[parseInt(d)])).join(', ')
    },

    getStatusColor (status) {
      const colors = {
        1: 'green',
        2: 'blue darken-2',
        3: 'purple darken-2',
        4: 'orange darken-2',
        5: 'brown',
        6: 'grey darken-3'
      }
      return colors[status] || 'grey'
    },

    getStatusText (status) {
      const statusObj = this.courtStatuses.find(s => s.value === status)
      return statusObj ? statusObj.text : this.$t('unknown')
    }
  }
}
</script>
